import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { safeLocalStorage } from "@/lib/storage";
import { useIsHydrated } from "./useIsHydrated";

// Early hydration renders (and the session skeletons) run before the dynamic
// route's query is parsed, so the org is briefly unknown even when the URL
// names one. Each resolved flag is therefore also cached under an org-less
// key that those renders fall back to.
const LAST_RESOLVED_ORG = "__last__";
export function readCachedOrgFlag(storageKeyPrefix: string, orgName: string | null): boolean {
  return safeLocalStorage.get(storageKeyPrefix + (orgName ?? LAST_RESOLVED_ORG)) === "true";
}

/**
 * Cached view of a per-installation flag that rides on the installations
 * query. On every page load there is a window where the flag reads `false`
 * and gated UI would pop in late; until the flag is known this falls back to
 * the value cached from the last visit (orgs never seen before assume
 * disabled), and caches the resolved value once the backend answers.
 */
export function useCachedOrgFlag(storageKeyPrefix, t0) {
  const $ = _c(9);
  const {
    orgName,
    enabled,
    enabledKnown
  } = t0;
  const isHydrated = useIsHydrated();
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== enabledKnown || $[2] !== orgName || $[3] !== storageKeyPrefix) {
    t1 = () => {
      if (!enabledKnown || !orgName) {
        return;
      }
      safeLocalStorage.set(storageKeyPrefix + orgName, String(enabled));
      safeLocalStorage.set(storageKeyPrefix + LAST_RESOLVED_ORG, String(enabled));
    };
    t2 = [storageKeyPrefix, enabledKnown, orgName, enabled];
    $[0] = enabled;
    $[1] = enabledKnown;
    $[2] = orgName;
    $[3] = storageKeyPrefix;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  if (enabledKnown) {
    return enabled;
  }
  if (!isHydrated) {
    return false;
  }
  let t3;
  if ($[6] !== orgName || $[7] !== storageKeyPrefix) {
    t3 = readCachedOrgFlag(storageKeyPrefix, orgName);
    $[6] = orgName;
    $[7] = storageKeyPrefix;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}